import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import classNames from "classnames";
import { useMediaQuery } from "usehooks-ts";
import Dialog from "./Dialog";
import Button from "./Button";
import Network from "../utils/Network";
import useToast from "../hooks/useToast";
import { getProfileURL } from "../utils/profile-url";
import { useAppTranslation } from "../i18n/useAppTranslation";
import "./GuildRequestTroopDialog.css";

// What GET /guild/raid-troops returns: the caller's guildmates and every troop they own.
export type GuildRaidTroop = {
  id: number;
  troopId: number;
  level: number;
  health: number;
  maxHealth: number;
  state: number;
  available: boolean;
  // adventure | arena | training | upgrading | forSale | notReady | notReleased | wounded
  reason?: string;
};

export type GuildRaidMember = {
  playerId: number;
  username: string;
  photoFile?: string;
  state: number;
  troops: GuildRaidTroop[];
};

type Props = {
  open: boolean;
  // The formation cell being asked for: { index, troopId?, level?, targetPriority? }.
  item: any;
  // The troop catalog (GET /troop), for icons and names.
  allTroops: any[];
  // The cell already holds a request, so it can be removed.
  canDelete: boolean;
  onChange: (item: any) => void;
  onSubmit: (item: any) => void;
  onDelete: () => void;
  onClose: () => void;
};

// The member's photo, or an empty avatar drawn here: the game's placeholder pictures carry a
// drop shadow, which leaves the frame off-centre inside a round crop.
const MemberAvatar = ({ playerId, photoFile }: { playerId: number; photoFile?: string }) => {
  const url = getProfileURL(playerId, photoFile);
  const [failed, setFailed] = useState(false);
  if (url && !failed) return <img className="grt-avatar" src={url} onError={() => setFailed(true)} alt="" />;
  return (
    <span className="grt-avatar grt-avatar-empty" aria-hidden>
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="9" r="4" />
        <path d="M4.5 20.5c0-4.1 3.4-6.5 7.5-6.5s7.5 2.4 7.5 6.5z" />
      </svg>
    </span>
  );
};

/**
 * Troop request of a guild raid. Instead of every troop in the game, the leader sees the members of
 * their guild and, beside them, the selected member's troops. Only troops that are ready and above
 * half health can be picked. Picking one asks for its type at its level or higher: any guild member
 * can still fill the request, not only the one whose troop was picked.
 */
const GuildRequestTroopDialog = ({ open, item, allTroops, canDelete, onChange, onSubmit, onDelete, onClose }: Props) => {
  const { t } = useAppTranslation(["common", "treasure"]);
  const openToast = useToast();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [inGuild, setInGuild] = useState(true);
  const [members, setMembers] = useState<GuildRaidMember[]>([]);
  const [memberId, setMemberId] = useState<number>();
  // The troop picked in this dialog, checked again before the request is made.
  const [picked, setPicked] = useState<{ memberId: number; troopId: number }>();
  // Phones get the stacked layout: members in a row that scrolls sideways, troops under them and
  // the target priority last. Same breakpoint as the stylesheet.
  const compact = useMediaQuery("(max-width: 576px)");
  const membersRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  async function load(): Promise<GuildRaidMember[] | undefined> {
    const response = await new Network().CallApi<{ inGuild: boolean; members: GuildRaidMember[] }>({
      url: "/guild/raid-troops",
      method: "get",
    });
    if (!response.ok) {
      setFailed(true);
      return undefined;
    }
    setFailed(false);
    setInGuild(response.data?.inGuild !== false);
    const list = response.data?.members || [];
    setMembers(list);
    return list;
  }

  useEffect(() => {
    if (!open) return;
    setPicked(undefined);
    setLoading(true);
    load().then((list) => {
      setLoading(false);
      if (!list) return;
      setMemberId((current) =>
        list.some((m) => m.playerId === current)
          ? current
          : (list.find((m) => m.troops.some((tr) => tr.available)) || list[0])?.playerId
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Whether the member row can scroll further back or forward, for its arrows. In right-to-left
  // languages scrollLeft runs from 0 down to minus the overflow, so only its size is compared.
  function updateEdges() {
    const row = membersRef.current;
    if (!row) return;
    const offset = Math.abs(row.scrollLeft);
    setEdges({ start: offset <= 1, end: offset >= row.scrollWidth - row.clientWidth - 1 });
  }

  function scrollMembers(toEnd: boolean) {
    const row = membersRef.current;
    if (!row) return;
    const rtl = getComputedStyle(row).direction === "rtl";
    const step = row.clientWidth * 0.75;
    row.scrollBy({ left: (toEnd !== rtl ? 1 : -1) * step, behavior: "smooth" });
  }

  // Keep the selected member in view in the row, also when the list opens on someone further along.
  useEffect(() => {
    if (!compact || loading) return;
    membersRef.current
      ?.querySelector<HTMLElement>(".grt-member.is-active")
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    updateEdges();
  }, [compact, loading, memberId, members]);

  const member = members.find((m) => m.playerId === memberId);
  const availableIn = (m?: GuildRaidMember) => m?.troops.filter((tr) => tr.available).length || 0;
  const catalog = (troopId?: number) => allTroops?.find((tr: any) => tr?.id === troopId);
  const requested = catalog(item?.troopId);

  function pick(troop: GuildRaidTroop) {
    if (!troop.available || !member) return;
    setPicked({ memberId: member.playerId, troopId: troop.id });
    onChange({ ...item, troopId: troop.troopId, level: troop.level });
  }

  async function submit() {
    if (!item?.troopId) return;
    if (!picked) {
      onSubmit(item);
      return;
    }
    // The troop may have left for an adventure or been hurt since the list was loaded.
    setChecking(true);
    const fresh = await load();
    setChecking(false);
    if (!fresh) {
      openToast({ ok: false, message: t("treasure:join.guild.loadFailed") });
      return;
    }
    const troop = fresh.find((m) => m.playerId === picked.memberId)?.troops.find((tr) => tr.id === picked.troopId);
    if (!troop?.available) {
      setPicked(undefined);
      onChange({ ...item, troopId: undefined, level: undefined });
      openToast({ ok: false, message: t("treasure:join.guild.noLongerAvailable") });
      return;
    }
    onSubmit(item);
  }

  function setPriority(value: number) {
    onChange({ ...item, targetPriority: value });
  }

  function renderMembers() {
    if (!members.length)
      return <div className="grt-empty">{t(inGuild ? "treasure:join.guild.noMembers" : "treasure:join.guild.notInGuild")}</div>;
    const list = (
      <div
        ref={membersRef}
        className="grt-members"
        role="listbox"
        aria-label={t("treasure:join.guild.membersTitle")}
        onScroll={compact ? updateEdges : undefined}
      >
        {members.map((m) => {
          const count = availableIn(m);
          return (
            <button
              key={m.playerId}
              type="button"
              role="option"
              aria-selected={m.playerId === memberId}
              className={classNames("grt-member", { "is-active": m.playerId === memberId })}
              onClick={() => setMemberId(m.playerId)}
            >
              <MemberAvatar playerId={m.playerId} photoFile={m.photoFile} />
              <span className="grt-member-text">
                <span className="grt-member-name">{m.username}</span>
                <span className="grt-member-count">{t("treasure:join.guild.availableCount", { count })}</span>
              </span>
              <span className={classNames("grt-dot", { "is-on": count > 0 })} aria-hidden />
            </button>
          );
        })}
      </div>
    );
    if (!compact) return list;
    // The arrows only page the row; swiping it works as well. They sit in reading order, so in
    // right-to-left languages the back arrow is on the right.
    const arrow = (toEnd: boolean) => (
      <button
        type="button"
        className={classNames("grt-arrow", { "is-next": toEnd })}
        aria-label={t(toEnd ? "treasure:join.guild.nextMembers" : "treasure:join.guild.previousMembers")}
        disabled={toEnd ? edges.end : edges.start}
        onClick={() => scrollMembers(toEnd)}
      >
        <svg viewBox="0 0 12 12" aria-hidden>
          <path d="M7.5 2 3.5 6l4 4" />
        </svg>
      </button>
    );
    return (
      <div className="grt-members-row">
        {arrow(false)}
        {list}
        {arrow(true)}
      </div>
    );
  }

  function renderTroops() {
    if (!member) return <div className="grt-empty">{members.length ? t("treasure:join.guild.pickMember") : ""}</div>;
    if (!member.troops.length) return <div className="grt-empty">{t("treasure:join.guild.noTroops")}</div>;
    return (
      <div className="grt-troops">
        {member.troops.map((troop) => {
          const info = catalog(troop.troopId);
          const health = troop.maxHealth > 0 ? Math.max(0, Math.min(100, (troop.health * 100) / troop.maxHealth)) : 0;
          const reason = troop.reason ? t(`treasure:join.guild.reasons.${troop.reason}`, troop.reason) : "";
          const label = `${info?.name || ""} ${t("treasure:join.guild.level", { level: troop.level })}${reason ? ` - ${reason}` : ""}`;
          return (
            <button
              key={troop.id}
              type="button"
              disabled={!troop.available}
              title={label}
              aria-label={label}
              aria-pressed={picked?.troopId === troop.id}
              className={classNames("grt-troop", { "is-selected": picked?.troopId === troop.id })}
              onClick={() => pick(troop)}
            >
              <img className="grt-troop-icon" src={info?.icon} alt="" draggable={false} />
              <span className="grt-troop-level">{t("treasure:join.guild.level", { level: troop.level })}</span>
              <span className={classNames("grt-hp", { "is-low": health <= 50 })} aria-hidden>
                <span style={{ width: `${health}%` }} />
              </span>
              {reason && <span className="grt-troop-reason">{reason}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  // Inside the troops panel on wider screens; its own box under the troops on phones.
  const priority = (
    <div className="grt-priority">
      <div className="grt-summary">
        {requested
          ? t("treasure:join.guild.requestSummary", { troop: requested.name, level: item?.level || 1 })
          : t("treasure:join.guild.pickTroop")}
      </div>
      <div className="grt-priority-title" id="grt-priority-title">
        {t("treasure:order.targetPriority")} :
      </div>
      <div className="grt-radios" role="radiogroup" aria-labelledby="grt-priority-title">
        {[
          [1, "closest"],
          [2, "lowestHp"],
          [3, "highestDamage"],
        ].map(([value, key]) => (
          <label key={value} className="grt-radio">
            <input
              type="radio"
              name="grt-priority"
              value={value}
              checked={item?.targetPriority === value}
              onChange={() => setPriority(value as number)}
            />
            <span className="grt-radio-mark" aria-hidden />
            {t(`treasure:order.priorities.${key}`)}
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      size="lg"
      dialogClassName="grt-dialog"
      title={t("treasure:join.requestTroopTitle")}
      submitLabel={t("treasure:join.request")}
      onSubmit={submit}
      disableSubmit={!item?.troopId || loading || checking}
      loading={checking}
      onClose={onClose}
    >
      <div className="grt-panels">
        <section className="grt-panel grt-panel-members">
          <h3 className="grt-plate">{t("treasure:join.guild.membersTitle")}</h3>
          {loading ? (
            <div className="grt-empty">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            renderMembers()
          )}
        </section>
        <section className="grt-panel grt-panel-troops" aria-live="polite">
          <h3 className="grt-bar">{t("treasure:join.guild.troopsTitle", { count: availableIn(member) })}</h3>
          {loading ? (
            <div className="grt-empty">
              <Spinner animation="border" size="sm" />
            </div>
          ) : failed ? (
            <div className="grt-empty">
              {t("treasure:join.guild.loadFailed")}
              <Button
                size="small"
                variant="wide"
                className="mx-auto mt-2"
                onClick={() => {
                  setLoading(true);
                  load().then((list) => {
                    setLoading(false);
                    if (list && !list.some((m) => m.playerId === memberId)) setMemberId(list[0]?.playerId);
                  });
                }}
              >
                {t("treasure:join.guild.retry")}
              </Button>
            </div>
          ) : (
            renderTroops()
          )}
          {!compact && priority}
        </section>
        {compact && priority}
      </div>

      {canDelete && (
        <div style={{ marginBottom: -60 }} className="text-center mt-2 w-100">
          <Button onClick={onDelete} variant="wide-secondary" className="mx-auto" style={{ minWidth: 70 }}>
            <img height={18} src={"/assets/images/icons/trash.svg"} alt="" />
            {t("treasure:join.deleteTroop")}
          </Button>
        </div>
      )}
    </Dialog>
  );
};

export default GuildRequestTroopDialog;
